import React from "react";
import { ChevronDown } from "lucide-react";

export default function BillingAccountSelect({ accounts, value, onChange, disabled }) {
  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState(0);
  const id = React.useId();
  const root = React.useRef(null);
  const options = [{ id: "", name: "Choisir une entreprise…" }, ...accounts];
  const selected = options.find((account) => account.id === value) || options[0];
  React.useEffect(() => {
    if (!open) return;
    const dismiss = (event) => { if (!root.current?.contains(event.target)) setOpen(false); };
    document.addEventListener("pointerdown", dismiss);
    return () => document.removeEventListener("pointerdown", dismiss);
  }, [open]);
  React.useEffect(() => {
    if (open) document.getElementById(`${id}-${active}`)?.scrollIntoView({ block: "nearest" });
  }, [open, active, id]);
  const choose = (account) => { onChange(account.id); setOpen(false); };
  const show = () => { setActive(Math.max(0, options.findIndex((account) => account.id === value))); setOpen(true); };
  return <div ref={root} className="relative min-w-0" onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false); }}>
    <button type="button" role="combobox" aria-label="Entreprise de facturation" aria-haspopup="listbox"
      aria-expanded={open && !disabled} aria-controls={open && !disabled ? id : undefined}
      aria-activedescendant={open && !disabled ? `${id}-${active}` : undefined} disabled={disabled}
      className="flex h-6 w-full min-w-0 items-center justify-between gap-2 rounded-[4px] border border-slate-300 bg-white px-2 text-left text-sm text-slate-700 hover:border-slate-400 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400"
      onClick={() => open ? setOpen(false) : show()}
      onKeyDown={(event) => {
        if (event.key === "Escape" && open) { event.preventDefault(); event.stopPropagation(); setOpen(false); }
        else if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
          event.preventDefault();
          if (!open) show();
          else setActive((index) => event.key === "Home" ? 0 : event.key === "End" ? options.length - 1 : Math.max(0, Math.min(options.length - 1, index + (event.key === "ArrowDown" ? 1 : -1))));
        } else if ((event.key === "Enter" || event.key === " ") && open) { event.preventDefault(); choose(options[active] || options[0]); }
      }}>
      <span className={`truncate ${selected.id ? "font-bold" : "font-normal"}`}>{selected.name}</span><ChevronDown size={14} className="shrink-0" />
    </button>
    {open && !disabled ? <ul id={id} role="listbox" aria-label="Entreprise de facturation"
      className="absolute left-0 right-0 top-full z-10 mt-1 max-h-48 overflow-auto rounded-[4px] border border-slate-300 bg-white p-1 shadow-lg">
      {options.map((account, index) => <li key={account.id} id={`${id}-${index}`} role="option" aria-selected={account.id === value}
        className={`cursor-pointer break-words rounded-[4px] px-2 py-1 text-sm font-normal ${index === active ? "bg-slate-100" : "hover:bg-slate-50"}`}
        onMouseDown={(event) => event.preventDefault()} onMouseEnter={() => setActive(index)} onClick={() => choose(account)}>
        {account.id ? <strong className="font-bold">{account.name}</strong> : account.name}
        {account.billing_address ? <span className="text-slate-500"> · {account.billing_address}</span> : null}
      </li>)}
    </ul> : null}
  </div>;
}
